/**
 * Modo lanzamiento (coming-soon) — flag runtime en base de datos.
 * El admin lo prende/apaga con un clic; el front lo lee en vivo (sin redeploy
 * ni cache: refetch periódico + al enfocar la pestaña).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

const LS_KEY = 'gurumba_launch_mode';

/** Cuánto tiempo confiamos en un cache que dice "modo expectativa ON".
 *  Un cache viejo con '1' (de cuando el sitio estaba cerrado) provocaba el
 *  parpadeo de coming-soon al volver un visitante. El '0' no caduca: si el
 *  flag se vuelve a prender, el RPC lo corrige en milisegundos. */
const TRUE_TTL_MS = 5 * 60_000;

function readCache(): boolean | undefined {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw === null) return undefined;
    // Formato nuevo: {"v":true,"t":1712345678901}
    if (raw.startsWith('{')) {
      const parsed = JSON.parse(raw) as { v?: boolean; t?: number };
      if (typeof parsed.v !== 'boolean') return undefined;
      if (!parsed.v) return false;
      const age = Date.now() - (parsed.t ?? 0);
      return age < TRUE_TTL_MS ? true : undefined;
    }
    // Formato viejo ('1' / '0'): solo aceptamos el '0'; el '1' pudo quedar
    // guardado desde antes del lanzamiento.
    return raw === '1' ? undefined : false;
  } catch {
    return undefined;
  }
}

function writeCache(on: boolean) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ v: on, t: Date.now() }));
  } catch { /* noop */ }
}

/** Lee el flag. Usa el último valor conocido (localStorage) para render
 *  instantáneo, y refresca en segundo plano. */
export function useLaunchMode() {
  return useQuery({
    queryKey: ['launch-mode'],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc('get_launch_mode');
      if (error) throw error;
      const on = Boolean(data);
      writeCache(on);
      return on;
    },
    initialData: readCache(),
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Admin: prende/apaga el modo expectativa. Aplica al instante. */
export function useSetLaunchMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (on: boolean) => {
      const { error } = await supabase.rpc('admin_set_launch_mode', { _on: on });
      if (error) throw new Error(error.message);
      return on;
    },
    onSuccess: (on) => {
      writeCache(on);
      qc.setQueryData(['launch-mode'], on);
      qc.invalidateQueries({ queryKey: ['launch-mode'] });
    },
  });
}
